import { z } from 'zod';
import { createServiceHttpClient } from '@wit-support/core-ports';
import { PaymentApiError } from './errors.js';
/**
 * Wire shapes of the wit-payment-service internal subscription API
 * (`/v3/payment/internal/subscriptions/*`). The whole upstream contract is
 * isolated to this file: tolerant Zod schemas for the `{ status, data }`
 * envelopes plus the URL builders. A future API change touches only here.
 *
 * Money stays in CENTS — these schemas never format or convert.
 */
const SUBSCRIPTION_STATUS = ['TRIAL', 'ACTIVE', 'PAST_DUE', 'UNPAID'];
const SUBSCRIPTION_PERIOD = ['DAY', 'WEEK', 'MONTH', 'YEAR'];
/**
 * A nullable billing period. Both `period` and `interval` are nullable upstream
 * (a subscription with no trial reports `{ period: null, interval: null }`).
 * Per-field `.catch(null)` tolerates a malformed value without fabricating one,
 * and the whole-object `.catch` below yields a real null pair rather than a fake
 * `{ period: 'DAY', interval: 0 }`.
 */
const BillingPeriodWire = z.object({
    period: z.enum(SUBSCRIPTION_PERIOD).nullable().catch(null),
    interval: z.number().nullable().catch(null),
});
const NULL_PERIOD = { period: null, interval: null };
const ActiveSubscriptionWire = z.object({
    subscriptionId: z.string(),
    stripeSubscriptionId: z.string().nullish().catch(undefined),
    gateway: z.string().nullish().catch(undefined),
    planKey: z.string().nullish().catch(undefined),
    status: z.enum(SUBSCRIPTION_STATUS).catch('ACTIVE'),
    trial: BillingPeriodWire.catch(NULL_PERIOD),
    billing: BillingPeriodWire.catch(NULL_PERIOD),
});
const MoneyBucketWire = z.object({
    succeededChargesCount: z.number().catch(0),
    grossAmount: z.number().catch(0),
    refundedAmount: z.number().catch(0),
    disputedAmount: z.number().catch(0),
    netAmount: z.number().catch(0),
});
const MoneyTotalWire = z.object({
    grossAmount: z.number().catch(0),
    refundedAmount: z.number().catch(0),
    disputedAmount: z.number().catch(0),
    netAmount: z.number().catch(0),
});
const BillingSummaryWire = z.object({
    userId: z.string(),
    currency: z.string().catch('usd'),
    multiCurrency: z.boolean().catch(false),
    currencies: z.array(z.string()).catch([]),
    subscriptions: z.object({
        activeCount: z.number().catch(0),
        activeByStatus: z.record(z.string(), z.number()).catch({}),
        cancelledCount: z.number().catch(0),
        active: z.array(ActiveSubscriptionWire).catch([]),
    }),
    billingCycles: z
        .object({
        passed: z.number().catch(0),
        succeeded: z.number().catch(0),
    })
        .catch({ passed: 0, succeeded: 0 }),
    money: z.object({
        subscriptions: MoneyBucketWire,
        additionalPurchases: MoneyBucketWire,
        total: MoneyTotalWire,
    }),
});
const BillingSummaryEnvelope = z.object({
    status: z.string(),
    data: BillingSummaryWire,
});
const CheckActiveEnvelope = z.object({
    status: z.string(),
    message: z.string().nullish().catch(undefined),
    // Decision-driving fields MUST NOT use .catch(): a malformed/missing value has to
    // throw so the worker prefetch fail-stops (subscription stays undefined) rather than
    // silently coercing a still-subscribed customer into a CONFIRMED { hasActive:false }.
    data: z.object({
        hasActiveSubscription: z.boolean(),
        subscriptionCount: z.number(),
    }),
});
const CancellationStateEnvelope = z.object({
    status: z.string(),
    // Decision-driving field: no .catch() — a malformed value must throw, not read as not-cancelled.
    data: z.object({
        isCancelled: z.boolean(),
    }),
});
const CancelResultWire = z.object({
    ok: z.boolean().catch(false),
    alreadyCancelled: z.boolean().catch(false),
    bypassed: z.boolean().nullish().catch(undefined),
    externalId: z.string().nullish().catch(undefined),
    gateway: z.string().nullish().catch(undefined),
});
const CancelResultEnvelope = z.object({
    status: z.string(),
    data: CancelResultWire,
});
const CancelAllItemWire = z.object({
    subscriptionId: z.string().nullish().catch(undefined),
    ok: z.boolean().catch(false),
    alreadyCancelled: z.boolean().nullish().catch(undefined),
    bypassed: z.boolean().nullish().catch(undefined),
    externalId: z.string().nullish().catch(undefined),
    gateway: z.string().nullish().catch(undefined),
    error: z.string().nullish().catch(undefined),
});
const CancelAllEnvelope = z.object({
    status: z.string(),
    data: z.object({
        total: z.number().catch(0),
        cancelled: z.number().catch(0),
        alreadyCancelled: z.number().catch(0),
        bypassed: z.number().catch(0),
        failed: z.number().catch(0),
        results: z.array(CancelAllItemWire).catch([]),
    }),
});
/**
 * Wire shape of `POST /v3/payment/internal/charges/{chargeId}/refund`'s
 * `data` — WRAPPED in the same `{ status: 'success', data: {...} }` envelope
 * as the subscription endpoints (the actual deployed contract; see
 * `docs/plans/2026-08-26-chargeblast-reactions/payment-charge-refund-openapi.yaml`).
 * Tolerant per field: this is a terminal-positive result either way
 * (`refunded` or `already_refunded`), so a malformed detail field must not
 * cost us the whole outcome. `providerStatus` is display/audit-only per the
 * spec (`'pending'` is terminal there, never polled), so it degrades to
 * `null` on anything unreadable rather than failing the whole parse.
 */
const RefundChargeResultWire = z.object({
    status: z.enum(['refunded', 'already_refunded']).catch('refunded'),
    refundId: z.string().nullable().catch(null),
    amountRefundedCents: z.number().nullable().catch(null),
    currency: z.string().nullable().catch(null),
    alreadyRefunded: z.boolean().catch(false),
    providerStatus: z.string().nullish().catch(null),
});
// `status` is a LITERAL, not `z.string()`: a 200 response whose envelope
// carries `{ status: 'error', ... }` (a malformed/misbehaving upstream that
// answers HTTP 200 on failure) must NOT parse as success. `.parse` throws on
// a mismatch, the thrown error is not a `PaymentApiError`, and
// `classifyRefundFailure` maps anything that isn't one to `indeterminate` —
// fail-closed, exactly like a network error, rather than silently reading a
// failure envelope as a completed refund.
const RefundChargeEnvelope = z.object({
    status: z.literal('success'),
    data: RefundChargeResultWire,
});
/**
 * The documented error body for a refund failure: `{ code, ... }`. `code` is
 * the field the caller branches escalation on (`classifyRefundFailure`); the
 * detail fields (`refundId`/`providerStatus` on `previous_refund_failed` and
 * `refund_requires_action`, `refundableCents` on `amount_exceeds_refundable`,
 * `chargeStatus` on `charge_not_refundable`) are NOT parsed here — they are
 * not consumed by anything in this package today (they belong to the reactor
 * in Task C, which reads them off `getRefund`'s own stored state, not off
 * this thrown error). Tolerant — a body we cannot read at all must still
 * produce a sane error rather than a parse crash (see `readJson`).
 */
const RefundErrorWire = z.object({
    code: z.string().nullish().catch(undefined),
    message: z.string().nullish().catch(undefined),
});
const toRefundChargeResult = (wire) => ({
    status: wire.status,
    refundId: wire.refundId,
    amountRefundedCents: wire.amountRefundedCents,
    currency: wire.currency,
    alreadyRefunded: wire.alreadyRefunded,
    providerStatus: wire.providerStatus ?? null,
});
const RetainingChoiceWire = z.object({
    step: z.string(),
    choiceId: z.number().catch(0),
    choiceTitle: z.string().catch(''),
    timestamp: z.string().catch(''),
});
const RetainingJourneyWire = z.object({
    startedAt: z.string().catch(''),
    updatedAt: z.string().catch(''),
    currentStep: z.string().catch(''),
    funnel: z.string().nullable().catch(null),
    completedSteps: z.array(z.string()).catch([]),
    choices: z.array(RetainingChoiceWire).catch([]),
});
const SubscriptionActionWire = z.object({
    id: z.string(),
    subscriptionId: z.string().catch(''),
    userId: z.string().catch(''),
    action: z.string(),
    // Anything other than a clean `success` reads as `error` — a malformed result
    // must never masquerade as a success in the support operator's view.
    result: z.enum(['success', 'error']).catch('error'),
    message: z.string().nullable().catch(null),
    // A malformed journey degrades to `null` (action shown without funnel context)
    // rather than dropping the whole record.
    retainingJourney: RetainingJourneyWire.nullable().catch(null),
    createdAt: z.string().catch(''),
});
const SubscriptionActionsEnvelope = z.object({
    status: z.string(),
    message: z.string().nullish().catch(undefined),
    data: z.object({
        userId: z.string().catch(''),
        returnedCount: z.number().catch(0),
        // Keep items RAW here and parse each element individually below: a single
        // malformed record (missing `id`/`action`) must drop ONLY that record, not
        // collapse the whole array to `[]` — array-level `.catch([])` would lose
        // every valid sibling. Consistent with the per-record `retainingJourney`
        // degradation above.
        items: z.array(z.unknown()).catch([]),
    }),
});
/**
 * Conditional-spread optionals so absent fields are omitted entirely (the codebase
 * runs `exactOptionalPropertyTypes`, so an optional must be absent, not `undefined`).
 * Mirrors the identity-resolver mapping pattern.
 */
const toBillingSummary = (wire) => ({
    userId: wire.userId,
    currency: wire.currency,
    multiCurrency: wire.multiCurrency,
    currencies: wire.currencies,
    subscriptions: {
        activeCount: wire.subscriptions.activeCount,
        activeByStatus: wire.subscriptions.activeByStatus,
        cancelledCount: wire.subscriptions.cancelledCount,
        active: wire.subscriptions.active.map((s) => ({
            subscriptionId: s.subscriptionId,
            ...(s.stripeSubscriptionId != null && { stripeSubscriptionId: s.stripeSubscriptionId }),
            ...(s.gateway != null && { gateway: s.gateway }),
            ...(s.planKey != null && { planKey: s.planKey }),
            status: s.status,
            trial: s.trial,
            billing: s.billing,
        })),
    },
    billingCycles: wire.billingCycles,
    money: {
        subscriptions: wire.money.subscriptions,
        additionalPurchases: wire.money.additionalPurchases,
        total: wire.money.total,
    },
});
const toCancelResult = (wire) => ({
    ok: wire.ok,
    alreadyCancelled: wire.alreadyCancelled,
    ...(wire.bypassed != null && { bypassed: wire.bypassed }),
    ...(wire.externalId != null && { externalId: wire.externalId }),
    ...(wire.gateway != null && { gateway: wire.gateway }),
});
const toCancelAllResult = (data) => ({
    total: data.total,
    cancelled: data.cancelled,
    alreadyCancelled: data.alreadyCancelled,
    bypassed: data.bypassed,
    failed: data.failed,
    results: data.results.map((r) => ({
        ...(r.subscriptionId != null && { subscriptionId: r.subscriptionId }),
        ok: r.ok,
        ...(r.alreadyCancelled != null && { alreadyCancelled: r.alreadyCancelled }),
        ...(r.bypassed != null && { bypassed: r.bypassed }),
        ...(r.externalId != null && { externalId: r.externalId }),
        ...(r.gateway != null && { gateway: r.gateway }),
        ...(r.error != null && { error: r.error }),
    })),
});
const toSubscriptionAction = (w) => ({
    id: w.id,
    subscriptionId: w.subscriptionId,
    userId: w.userId,
    action: w.action,
    result: w.result,
    message: w.message,
    retainingJourney: w.retainingJourney == null
        ? null
        : {
            startedAt: w.retainingJourney.startedAt,
            updatedAt: w.retainingJourney.updatedAt,
            currentStep: w.retainingJourney.currentStep,
            funnel: w.retainingJourney.funnel,
            completedSteps: w.retainingJourney.completedSteps,
            choices: w.retainingJourney.choices.map((c) => ({
                step: c.step,
                choiceId: c.choiceId,
                choiceTitle: c.choiceTitle,
                timestamp: c.timestamp,
            })),
        },
    createdAt: w.createdAt,
});
const MATCH_SIGNALS = [
    'card_last4',
    'wallet_last4',
    'email',
    'billing_email',
    'name',
];
/**
 * One card of a candidate. Tolerant per FIELD and parsed per ELEMENT below, so a
 * garbled card costs only itself — losing the whole array would take away the
 * `walletLast4` that explains why a `wallet_last4` match is in the list at all.
 */
const CustomerCardWire = z.object({
    brand: z.string().nullish().catch(null),
    last4: z.string().nullish().catch(null),
    walletType: z.string().nullish().catch(null),
    walletLast4: z.string().nullish().catch(null),
    chargesCount: z.number().nullish().catch(null),
    lastUsedAt: z.string().nullish().catch(null),
});
/**
 * One candidate of the customer-search envelope. Every field is tolerant: this
 * is display data for an operator, so a malformed `succeededChargesCount` must
 * not cost us the row that carries the right `stripeCustomerId`.
 *
 * `matchedBy` is read as raw strings and filtered below rather than parsed as an
 * enum array: an unknown signal added upstream must drop that ONE entry, not the
 * whole array (`z.array(z.enum(...)).catch(undefined)` would lose the valid ones).
 */
const CustomerCandidateWire = z.object({
    internalUserId: z.string().nullish().catch(undefined),
    stripeCustomerId: z.string().nullish().catch(undefined),
    email: z.string().nullish().catch(undefined),
    billingEmail: z.string().nullish().catch(undefined),
    billingName: z.string().nullish().catch(undefined),
    cardBrand: z.string().nullish().catch(undefined),
    cardLast4: z.string().nullish().catch(undefined),
    succeededChargesCount: z.number().nullish().catch(undefined),
    lastChargeAt: z.string().nullish().catch(undefined),
    matchedBy: z.array(z.string()).nullish().catch(undefined),
    // `null` is upstream's own value for «no name in the query», and is preserved;
    // only a MISSING field maps to «absent». A malformed number degrades to null
    // rather than dropping the candidate — the score is display data.
    nameScore: z.number().nullable().catch(null).optional(),
    // Absent on an upstream that predates the flag → `false` at the mapping site:
    // «we were not told the name is unknown» is the same as «the name is known»
    // for display purposes, and inventing `true` would put a warning on every row.
    nameUnknown: z.boolean().nullish().catch(undefined),
    cards: z.array(z.unknown()).nullish().catch(undefined),
});
/**
 * A boolean SAFEGUARD flag on the envelope. Three cases, deliberately distinct:
 * absent (an upstream that predates the field) reads as `false`, an explicit
 * boolean is taken as-is, and a MALFORMED value reads as `true` — fail-closed,
 * because both flags exist to say «this answer is not the whole truth» and a
 * garbled one cannot be read as reassurance. `.catch(true)` only fires on the
 * malformed branch: `undefined`/`null` satisfy the union and fall through to the
 * `?? false` at the mapping site.
 */
const SafeguardFlagWire = z.union([z.boolean(), z.null(), z.undefined()]).catch(true);
const CustomerSearchEnvelope = z.object({
    status: z.string(),
    message: z.string().nullish().catch(undefined),
    data: z.object({
        // Items stay RAW and are parsed per element below — same reason as
        // `SubscriptionActionsEnvelope`: one malformed candidate must drop only itself.
        items: z.array(z.unknown()).catch([]),
        total: z.number().nullish().catch(undefined),
        returnedCount: z.number().nullish().catch(undefined),
        truncated: SafeguardFlagWire,
        nameMatchRelaxed: SafeguardFlagWire,
        // Counters, not safeguards: a missing one means «не знаем» (`null`), never
        // zero — zero is the one value that would suppress the name-less retry.
        totalWithoutName: z.number().nullish().catch(undefined),
        nameFilteredOut: z.number().nullish().catch(undefined),
    }),
});
/** Upstream's machine-readable `code` on a 400. Ours to branch on, not `message`. */
const SEARCH_ERROR_CODES = [
    'SEARCH_CRITERIA_REQUIRED',
    'NAME_ONLY_SEARCH_UNSUPPORTED',
    'INVALID_LAST4',
    'INVALID_EMAIL',
    'INVALID_NAME',
    'INVALID_BRAND',
    'INVALID_SEARCH_PARAM',
];
/**
 * The 400 body. Tolerant on purpose — `code` is what we branch on, and a body we
 * cannot read at all must still produce a sane error rather than a parse crash.
 */
const SearchErrorWire = z.object({
    status: z.string().nullish().catch(undefined),
    message: z.string().nullish().catch(undefined),
    code: z.enum(SEARCH_ERROR_CODES).nullish().catch(undefined),
});
/**
 * Map one wire candidate. `matchedBy` falls back to the criteria WE sent when
 * upstream omits it — never to a signal we did not search by, because the panel
 * shows this line as «почему он здесь». Returns `null` for a candidate carrying
 * neither an internal user id nor a Stripe customer id: there is nothing to show
 * and nothing to link.
 */
const toCustomerCandidate = (wire, fallbackSignals) => {
    if (wire.internalUserId == null && wire.stripeCustomerId == null)
        return null;
    const matchedBy = (wire.matchedBy ?? []).filter((s) => MATCH_SIGNALS.includes(s));
    // Per-element like `items`: a malformed card drops itself, the rest survive.
    const cards = wire.cards?.flatMap((c) => {
        const r = CustomerCardWire.safeParse(c);
        return r.success
            ? [
                {
                    brand: r.data.brand ?? null,
                    last4: r.data.last4 ?? null,
                    walletType: r.data.walletType ?? null,
                    walletLast4: r.data.walletLast4 ?? null,
                    chargesCount: r.data.chargesCount ?? null,
                    lastUsedAt: r.data.lastUsedAt ?? null,
                },
            ]
            : [];
    });
    return {
        witUserId: wire.internalUserId ?? null,
        ...(wire.stripeCustomerId != null && { stripeCustomerId: wire.stripeCustomerId }),
        ...(wire.email != null && { email: wire.email }),
        ...(wire.billingEmail != null && { billingEmail: wire.billingEmail }),
        ...(wire.billingName != null && { displayName: wire.billingName }),
        ...(wire.cardBrand != null && { cardBrand: wire.cardBrand }),
        ...(wire.cardLast4 != null && { cardLast4: wire.cardLast4 }),
        ...(wire.succeededChargesCount != null && {
            succeededChargesCount: wire.succeededChargesCount,
        }),
        ...(wire.lastChargeAt != null && { lastChargeAt: wire.lastChargeAt }),
        // The fallback describes what WE searched by, so it must not assert a name
        // match on a candidate upstream says it never compared: `nameUnknown` means
        // the row is in the answer BECAUSE the name was unknown, and «совпало по
        // имени» right next to that is a contradiction the operator cannot resolve.
        matchedBy: matchedBy.length > 0
            ? matchedBy
            : wire.nameUnknown
                ? fallbackSignals.filter((s) => s !== 'name')
                : fallbackSignals,
        // `null` is meaningful here («no name was queried») and is kept; only a field
        // upstream never sent is omitted.
        ...(wire.nameScore !== undefined && { nameScore: wire.nameScore }),
        nameUnknown: wire.nameUnknown ?? false,
        ...(cards != null && { cards }),
    };
};
/**
 * Read a body we are only going to inspect, never trust. A 400 can also come
 * from a gateway as HTML, and a `SyntaxError` out of `res.json()` would replace
 * the precise «upstream rejected our query» error with an opaque parse crash.
 */
const readJson = async (res) => {
    try {
        return await res.json();
    }
    catch {
        return undefined;
    }
};
/** Trim and drop blanks — a whitespace-only field is not a search criterion. */
const trimmed = (v) => {
    const t = v?.trim();
    return t != null && t.length > 0 ? t : undefined;
};
/**
 * HTTP adapter for `PaymentPort`, backed by the wit-payment-service internal
 * subscription endpoints. The wire shape is isolated to the schemas + URL
 * builders above, so confirming/altering the real endpoint touches only this
 * file.
 */
export const createHttpPaymentPort = (opts) => {
    const client = createServiceHttpClient({
        baseUrl: opts.baseUrl,
        acceptJson: true,
        ...(opts.serviceKey !== undefined && { apiKey: opts.serviceKey }),
        ...(opts.fetchFn !== undefined && { fetchFn: opts.fetchFn }),
        ...(opts.timeoutMs !== undefined && { timeoutMs: opts.timeoutMs }),
    });
    const get = (path) => client.get(path);
    const post = (path, opts2) => {
        const body = {};
        if (opts2?.reason != null)
            body['reason'] = opts2.reason;
        if (opts2?.actor != null)
            body['actor'] = opts2.actor;
        const hasBody = Object.keys(body).length > 0;
        return client.post(path, hasBody ? body : undefined);
    };
    return {
        async getBillingSummary(userId) {
            const res = await get(`/v3/payment/internal/subscriptions/billing-summary/${encodeURIComponent(userId)}`);
            if (res.status === 404)
                return null;
            if (!res.ok) {
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status} for billing-summary`);
            }
            const parsed = BillingSummaryEnvelope.parse(await res.json());
            return toBillingSummary(parsed.data);
        },
        async checkActive(userId) {
            const res = await get(`/v3/payment/internal/subscriptions/check/${encodeURIComponent(userId)}`);
            if (!res.ok) {
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status} for check`);
            }
            const parsed = CheckActiveEnvelope.parse(await res.json());
            return {
                hasActive: parsed.data.hasActiveSubscription,
                count: parsed.data.subscriptionCount,
            };
        },
        async getCancellationState(userId) {
            const res = await get(`/v3/payment/internal/subscriptions/cancellation-state/${encodeURIComponent(userId)}`);
            if (!res.ok) {
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status} for cancellation-state`);
            }
            const parsed = CancellationStateEnvelope.parse(await res.json());
            return { isCancelled: parsed.data.isCancelled };
        },
        async getSubscriptionActions(userId, opts2) {
            const qs = new URLSearchParams();
            qs.set('limit', String(opts2?.limit ?? 50));
            if (opts2?.result != null)
                qs.set('result', opts2.result);
            if (opts2?.action != null)
                qs.set('action', opts2.action);
            const res = await get(`/v3/payment/internal/subscriptions/actions/${encodeURIComponent(userId)}?${qs.toString()}`);
            if (res.status === 404)
                return [];
            if (!res.ok) {
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status} for actions`);
            }
            const parsed = SubscriptionActionsEnvelope.parse(await res.json());
            // Per-element tolerant: drop only the records that fail to parse.
            return parsed.data.items.flatMap((item) => {
                const r = SubscriptionActionWire.safeParse(item);
                return r.success ? [toSubscriptionAction(r.data)] : [];
            });
        },
        async searchCustomers(query) {
            const last4 = trimmed(query.last4);
            const name = trimmed(query.name);
            const email = trimmed(query.email);
            const brand = trimmed(query.brand);
            // No criterion → no request. Reported as `unavailable`, not as an empty
            // `ok`: we did not search, so we must not let the panel render «ничего не
            // найдено», and not as a throw either — an operator opening the panel on a
            // conversation from which nothing was extracted is a normal state, not an
            // error the caller should have to catch.
            if (last4 == null && name == null && email == null) {
                return { status: 'unavailable', reason: 'empty_query' };
            }
            const qs = new URLSearchParams();
            if (last4 != null)
                qs.set('last4', last4);
            if (name != null)
                qs.set('name', name);
            if (email != null)
                qs.set('email', email);
            // `brand` narrows a card search only; alone it is not a criterion, so it is
            // sent only alongside `last4`.
            if (brand != null && last4 != null)
                qs.set('brand', brand);
            qs.set('limit', String(Math.min(Math.max(Math.trunc(query.limit ?? 10), 1), 50)));
            const res = await get(`/v3/payment/internal/customers/search?${qs.toString()}`);
            // The route does not exist yet on the adjacent team's side. 404 here means
            // «no such endpoint», NOT «no such customer» — the agreed contract answers
            // 200 with `items: []` for a search that found nobody, precisely so the two
            // stay distinguishable. 405/501 cover a gateway that knows the path but not
            // the method / has it unimplemented.
            if (res.status === 404 || res.status === 405 || res.status === 501) {
                return { status: 'unavailable', reason: 'endpoint_not_deployed' };
            }
            // A 400 is upstream REFUSING this particular query — never a transient
            // fault, so it must not be retried or alerted on. Exactly one of the codes
            // is a legitimate steady state rather than a bug on our side: a name-only
            // search is unbuilt upstream («фаза 2»), and the operator has to read
            // «поиск только по имени пока не включён», not «поиск недоступен». Every
            // other code means WE sent a query upstream's own validation rejects, i.e.
            // our bug — which throws, carrying `code` so the log names the field.
            if (res.status === 400) {
                const err = SearchErrorWire.safeParse(await readJson(res));
                const code = err.success ? err.data.code : undefined;
                if (code === 'NAME_ONLY_SEARCH_UNSUPPORTED') {
                    return { status: 'unavailable', reason: 'name_only_unsupported' };
                }
                throw new PaymentApiError(400, `payment: HTTP 400 (${code ?? 'unknown code'}) for customer-search`);
            }
            if (!res.ok) {
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status} for customer-search`);
            }
            const fallbackSignals = [
                // `last4` maps to `card_last4` and NOT to `wallet_last4`: upstream matches
                // it against both, but this fallback fires only when upstream omitted
                // `matchedBy` entirely, and guessing the wallet would assert something we
                // have no evidence for. Real answers always carry `matchedBy`.
                ...(last4 != null ? ['card_last4'] : []),
                ...(name != null ? ['name'] : []),
                ...(email != null ? ['email'] : []),
            ];
            const parsed = CustomerSearchEnvelope.parse(await res.json());
            const candidates = parsed.data.items.flatMap((item) => {
                const r = CustomerCandidateWire.safeParse(item);
                if (!r.success)
                    return [];
                const candidate = toCustomerCandidate(r.data, fallbackSignals);
                return candidate == null ? [] : [candidate];
            });
            return {
                status: 'ok',
                candidates,
                // A missing/garbled `total` must not under-report the page we already
                // hold, so it floors at the number of candidates we actually mapped.
                total: Math.max(parsed.data.total ?? candidates.length, candidates.length),
                // Upstream's own count of the page, NOT `candidates.length` — the two
                // differ exactly when a candidate was dropped as unmappable, and that gap
                // is worth being able to see.
                returnedCount: parsed.data.returnedCount ?? candidates.length,
                truncated: parsed.data.truncated ?? false,
                nameMatchRelaxed: parsed.data.nameMatchRelaxed ?? false,
                totalWithoutName: parsed.data.totalWithoutName ?? null,
                nameFilteredOut: parsed.data.nameFilteredOut ?? null,
            };
        },
        async cancelAll(userId, opts2) {
            const res = await post(`/v3/payment/internal/subscriptions/cancel-all/${encodeURIComponent(userId)}`, opts2);
            if (!res.ok) {
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status} for cancel-all`);
            }
            const parsed = CancelAllEnvelope.parse(await res.json());
            return toCancelAllResult(parsed.data);
        },
        async cancelOne(subscriptionId, opts2) {
            const res = await post(`/v3/payment/internal/subscriptions/cancel/${encodeURIComponent(subscriptionId)}`, opts2);
            if (!res.ok) {
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status} for cancel`);
            }
            const parsed = CancelResultEnvelope.parse(await res.json());
            return toCancelResult(parsed.data);
        },
        async cancelByStripeId(stripeId, opts2) {
            const res = await post(`/v3/payment/internal/subscriptions/cancel-by-stripe-id/${encodeURIComponent(stripeId)}`, opts2);
            if (!res.ok) {
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status} for cancel-by-stripe-id`);
            }
            const parsed = CancelResultEnvelope.parse(await res.json());
            return toCancelResult(parsed.data);
        },
        async cancelAllByCustomer(stripeCustomerId, opts2) {
            const res = await post(`/v3/payment/internal/subscriptions/cancel-all-by-customer/${encodeURIComponent(stripeCustomerId)}`, opts2);
            if (!res.ok) {
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status} for cancel-all-by-customer`);
            }
            const parsed = CancelAllEnvelope.parse(await res.json());
            return toCancelAllResult(parsed.data);
        },
        async refundCharge(refundOpts) {
            const body = {
                idempotencyKey: refundOpts.idempotencyKey,
            };
            if (refundOpts.amountCents != null)
                body['amountCents'] = refundOpts.amountCents;
            if (refundOpts.reason != null)
                body['reason'] = refundOpts.reason;
            if (refundOpts.actor != null)
                body['actor'] = refundOpts.actor;
            const res = await client.post(`/v3/payment/internal/charges/${encodeURIComponent(refundOpts.chargeId)}/refund`, body);
            if (!res.ok) {
                // The body carries a machine-readable `code` (`dispute_open`,
                // `charge_not_found`, `amount_exceeds_refundable`,
                // `charge_not_refundable`, `previous_refund_failed`,
                // `refund_requires_action`, `refund_status_unrecognized`, `INVALID_*`)
                // — set on `PaymentApiError.code` (not just folded into the message)
                // so `classifyRefundFailure` can branch on it directly, and still
                // folded into the message for anything that only logs `.message`.
                const err = RefundErrorWire.safeParse(await readJson(res));
                const code = err.success ? (err.data.code ?? null) : null;
                throw new PaymentApiError(res.status, `payment: HTTP ${res.status}${code != null ? ` (${code})` : ''} for refund`, code);
            }
            // The refund endpoint's 200 body is WRAPPED (`{ status, data }`), unlike
            // the earlier assumption of a bare object — unwrap `data` before mapping.
            const parsed = RefundChargeEnvelope.parse(await res.json());
            return toRefundChargeResult(parsed.data);
        },
    };
};
//# sourceMappingURL=http-payment.js.map